import clsx from "clsx";
import { Check, CircleHelp, MountainSnow, X } from "lucide-react";
import { type ReactNode, useId, useRef, useState } from "react";

import { designate } from "../../components/attention";
import { Disclosure } from "../../components/panel";
import { Button, IconButton, Kbd, KbdHint } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { locale, t } from "../../i18n/i18n";
import type { Draft } from "../../state/ui";
import type { BannerLayout } from "../manoeuvre/freeBands";
import { ringAreaKm2 } from "./geometry";
import { SymbolThumb } from "../annotations/AnnotationMenu";

/** Bandeau du tracé en cours, en haut de la carte : ce qu'on dessine, le geste attendu (au doigt : « Touchez… »,
 *  sans touche de clavier), et les boutons qui remplacent le double-clic et les touches (écran tactile).
 *
 *  `layout` (`freeBands.bannerPlace`, selon la bande libre entre les panneaux, comme le bandeau Manœuvre) :
 *  « full » consigne et boutons en une rangée ; « compact » consigne repliée derrière « ? » ; « mini » boutons en
 *  icônes seules, nommées et bullées. « Terminer » ou « Analyser » restent cliquables avant le nombre de points
 *  voulu (`blocked`) : leur clic déplie la consigne et la fait clignoter (R12). */
export function DraftBanner({
  draft,
  onFinish,
  onCancel,
  layout = "full",
}: {
  draft: Draft;
  onFinish: () => void;
  onCancel: () => void;
  layout?: BannerLayout;
}) {
  const coarse = useMediaQuery("(pointer: coarse)");
  if (draft.purpose === "terrain") return <ZoneBanner draft={draft} onFinish={onFinish} onCancel={onCancel} layout={layout} coarse={coarse} />;
  if (draft.family === "annotation")
    return (
      <DrawingBanner
        layout={layout}
        icon={draft.sidc ? <SymbolThumb sidc={draft.sidc} /> : undefined}
        title={t("Nouvelle annotation : {name}", { name: draft.name })}
        hint={
          coarse
            ? t("Touchez la carte à l'endroit de l'annotation. Ses détails se saisissent ensuite dans la fiche.")
            : t("Cliquez sur la carte à l'endroit de l'annotation. Ses détails se saisissent ensuite dans la fiche.")
        }
        onCancel={onCancel}
      />
    );
  const line = draft.family === "lineGraphics";
  const count = draft.points.length;
  const hint = line
    ? count === 0
      ? coarse
        ? t("Touchez la carte pour poser le premier point.")
        : t("Cliquez sur la carte pour poser le premier point.")
      : count === 1
        ? coarse
          ? t("Touchez la carte pour poser le point suivant.")
          : t("Cliquez pour poser le point suivant.")
        : coarse
          ? t("{n} points posés : touchez « Terminer » pour finir la ligne.", { n: count })
          : t("{n} points posés : double-cliquez sur le dernier point, ou appuyez sur Entrée, pour terminer.", { n: count })
    : draft.family === "cbrnPlumes"
      ? coarse
        ? t("Touchez le point de relâchement. Direction et vent se règlent ensuite dans la fiche.")
        : t("Cliquez sur le point de relâchement. Direction et vent se règlent ensuite dans la fiche.")
      : coarse
        ? t("Touchez le centre de la zone. Le rayon se règle ensuite dans la fiche.")
        : t("Cliquez sur le centre de la zone. Le rayon se règle ensuite dans la fiche.");
  return (
    <DrawingBanner
      layout={layout}
      icon={<span className="size-3 shrink-0 rounded-sm" style={{ background: draft.color }} aria-hidden />}
      title={t("Nouveau graphique : {name}", { name: draft.name })}
      hint={
        <>
          {hint}
          {line && count > 0 && !coarse && (
            <KbdHint className="ml-1 whitespace-nowrap">
              <Kbd>⌫</Kbd> {t("efface le dernier point.")}
            </KbdHint>
          )}
        </>
      }
      finish={
        line
          ? { label: t("Terminer"), title: t("Terminer la ligne (Entrée)"), blocked: count < 2, onClick: onFinish }
          : undefined
      }
      onCancel={onCancel}
    />
  );
}

/** Tracé de la zone à analyser : consigne, surface en direct, « Analyser » dès trois sommets ; un tracé croisé est
 *  signalé (en rouge d'erreur) et le tracé continue. */
function ZoneBanner({
  draft,
  onFinish,
  onCancel,
  layout,
  coarse,
}: {
  draft: Draft;
  onFinish: () => void;
  onCancel: () => void;
  layout: BannerLayout;
  coarse: boolean;
}) {
  const count = draft.points.length;
  const ring = draft.hover && count >= 2 ? [...draft.points, draft.hover] : draft.points;
  const area = ring.length >= 3 ? ringAreaKm2(ring) : null;
  const hint =
    count < 3
      ? coarse
        ? t("Touchez la carte pour poser les sommets de la zone ({n} sur 3 au moins).", { n: count })
        : t("Cliquez sur la carte pour poser les sommets de la zone ({n} sur 3 au moins).", { n: count })
      : coarse
        ? t("Touchez le premier sommet, ou « Analyser », pour analyser la zone.")
        : t("Cliquez sur le premier sommet, double-cliquez ou appuyez sur Entrée pour analyser.");
  return (
    <DrawingBanner
      layout={layout}
      icon={<MountainSnow size={16} className="shrink-0 text-ink-soft" aria-hidden />}
      title={t("Zone à analyser")}
      extra={
        area !== null && (
          <span className="font-mono text-meta font-medium whitespace-nowrap text-ink-soft tabular-nums">
            ≈ {area.toLocaleString(locale(), { maximumFractionDigits: area < 10 ? 1 : 0 })} km²
          </span>
        )
      }
      hint={
        <>
          {hint}
          {count > 0 && !coarse && (
            <KbdHint className="ml-1 whitespace-nowrap">
              <Kbd>⌫</Kbd> {t("efface le dernier sommet.")}
            </KbdHint>
          )}
        </>
      }
      error={draft.error}
      finish={{ label: t("Analyser"), title: t("Analyser la zone (Entrée)"), blocked: count < 3, onClick: onFinish }}
      onCancel={onCancel}
    />
  );
}

/** Corps commun des deux bandeaux de tracé, dans les trois dispositions. */
export function DrawingBanner({
  layout,
  icon,
  title,
  extra,
  hint,
  error,
  finish,
  onCancel,
}: {
  layout: BannerLayout;
  icon: ReactNode;
  title: string;
  extra?: ReactNode;
  hint: ReactNode;
  error?: string;
  finish?: { label: string; title: string; blocked: boolean; onClick: () => void };
  onCancel: () => void;
}) {
  const compact = layout !== "full";
  const mini = layout === "mini";
  const [hintOpen, setHintOpen] = useState(false);
  const hintId = useId();
  const hintRef = useRef<HTMLDivElement>(null);
  const showHint = !compact || hintOpen;

  // Action bloquée (pas assez de points) : la consigne se déplie et clignote, elle dit le geste attendu.
  const explain = () => {
    if (compact) setHintOpen(true);
    window.setTimeout(() => designate(hintRef.current), compact && !hintOpen ? 120 : 0);
  };

  const hintText = (
    <div ref={hintRef} className={clsx("rounded-md text-meta leading-snug text-ink-muted", compact && "pt-1 pr-1.5 pb-0.5")}>
      {hint}
    </div>
  );
  const errorText = error && <div className="mt-0.5 text-meta font-medium text-danger">{error}</div>;

  const finishButton =
    finish &&
    (mini ? (
      <IconButton
        variant="primary"
        size="sm"
        blocked={finish.blocked}
        onBlocked={explain}
        onClick={finish.onClick}
        aria-label={finish.label}
        title={finish.title}
      >
        <Check size={14} />
      </IconButton>
    ) : (
      <Button blocked={finish.blocked} onBlocked={explain} onClick={finish.onClick} title={finish.title} size="sm">
        <Check size={14} /> {finish.label}
      </Button>
    ));
  const cancelButton = mini ? (
    <IconButton variant="outline" size="sm" onClick={onCancel} aria-label={t("Annuler")} title={t("Abandonner le tracé (Échap)")}>
      <X size={14} />
    </IconButton>
  ) : (
    <Button variant="secondary" onClick={onCancel} title={t("Abandonner le tracé (Échap)")} size="sm">
      <X size={14} /> {t("Annuler")}
    </Button>
  );
  const helpToggle = compact && (
    <IconButton
      size="sm"
      aria-expanded={hintOpen}
      aria-controls={hintId}
      aria-label={hintOpen ? t("Masquer la consigne") : t("Afficher la consigne")}
      title={hintOpen ? t("Masquer la consigne") : t("Afficher la consigne")}
      active={hintOpen}
      onClick={() => setHintOpen((open) => !open)}
    >
      <CircleHelp size={14} />
    </IconButton>
  );

  if (!compact)
    return (
      <div
        role="status"
        data-attention="draft-banner"
        className="pointer-events-auto flex max-w-[min(680px,calc(100vw-2rem))] items-center gap-3 rounded-xl border border-line bg-surface/95 py-2 pr-2 pl-3 shadow-float backdrop-blur"
      >
        {icon}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 text-ui font-semibold text-ink">
            {title}
            {extra}
          </div>
          {hintText}
          {errorText}
        </div>
        {finishButton}
        {cancelButton}
      </div>
    );

  return (
    <div
      role="status"
      data-attention="draft-banner"
      className={clsx(
        "pointer-events-auto flex max-w-full flex-col rounded-xl border border-line bg-surface/95 shadow-float backdrop-blur",
        mini ? "p-1" : "py-1.5 pr-1.5 pl-3",
      )}
    >
      <div className={clsx("flex flex-wrap items-center", mini ? "justify-center gap-1" : "gap-x-2 gap-y-1")}>
        <div className={clsx("flex min-w-0 items-center gap-2", !mini && "flex-1")}>
          {mini ? (
            <span className="inline-flex size-7 shrink-0 items-center justify-center" title={title}>
              {icon}
            </span>
          ) : (
            <>
              {icon}
              <span className="min-w-0 truncate text-ui font-semibold text-ink">{title}</span>
            </>
          )}
          {!mini && extra}
          {helpToggle}
        </div>
        <div className={clsx("flex items-center gap-1", !mini && "ml-auto")}>
          {finishButton}
          {cancelButton}
        </div>
      </div>
      <Disclosure open={showHint} id={hintId}>
        {hintText}
      </Disclosure>
      {errorText && <div className={mini ? "px-1" : "pr-1.5"}>{errorText}</div>}
    </div>
  );
}
